/**
 * ConversionCrudViewEditRejectPermissionScopePage — per-role conversion permission scope.
 * Header + create form (conversion_id/role/action/allowed/reason) + filterable list.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useConversionCrudViewEditRejectPermissionScopeCreate,
	useConversionCrudViewEditRejectPermissionScopeList,
} from "./hooks";
import type { ScopeAction, ScopeRole } from "./api";

const ROLES: ScopeRole[] = ["advertiser", "manager", "affiliate", "admin"];
const ACTIONS: ScopeAction[] = ["view", "edit", "reject"];

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const PILL_ALLOW =
	"rounded-[6px] border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700";
const PILL_DENY =
	"rounded-[6px] border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700";

function fmtDate(secs: number): string {
	if (!Number.isFinite(secs) || secs <= 0) return "—";
	const d = new Date(secs * 1000);
	return d.toISOString().replace("T","").slice(0, 19);
}

export function ConversionCrudViewEditRejectPermissionScopePage() {
	const [filterConversionId, setFilterConversionId] = useState("");
	const [filterRole, setFilterRole] = useState<"" | ScopeRole>("");
	const [filterAction, setFilterAction] = useState<"" | ScopeAction>("");

	const activeFilters = useMemo(
 () => ({
 ...(filterConversionId ? { conversion_id: filterConversionId } : {}),
 ...(filterRole ? { role: filterRole } : {}),
 ...(filterAction ? { action: filterAction } : {}),
 }),
 [filterConversionId, filterRole, filterAction],
	);

	const listQuery = useConversionCrudViewEditRejectPermissionScopeList(activeFilters);
	const createMut = useConversionCrudViewEditRejectPermissionScopeCreate();

	const [conversionId, setConversionId] = useState("");
	const [role, setRole] = useState<ScopeRole>("advertiser");
	const [action, setAction] = useState<ScopeAction>("view");
	const [allowed, setAllowed] = useState<boolean>(true);
	const [reason, setReason] = useState("");

	const handleCreate = (e: FormEvent) => {
 e.preventDefault();
 if (!conversionId.trim()) return;
 createMut.mutate(
 {
 conversion_id: conversionId.trim(),
 role,
 action,
 allowed,
 reason: reason.trim() || null,
 },
 {
 onSuccess: () => {
 setConversionId("");
 setReason("");
 },
 },
 );
	};

	const items = listQuery.data?.items ?? [];

	return (
 <main
 data-testid="conversion-crud-view-edit-reject-permission-scope-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 conversion-crud-view-edit-reject-permission-scope
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Conversion Permission Scope
 </h1>
 <p className="text-sm text-zinc-600">
 Per-role view / edit / reject permissions per conversion.
 </p>
 </header>

 <section
 className={CARD}
 aria-label="Create conversion permission scope"
 >
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Create rule</h2>
 <form
 onSubmit={handleCreate}
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
 data-testid="conversion-crud-view-edit-reject-permission-scope-create-form"
 >
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 Conversion ID
 <input
 type="text"
 className={INPUT}
 placeholder="conv_123"
 value={conversionId}
 onChange={(e) => setConversionId(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="conversion-crud-view-edit-reject-permission-scope-conversion-id-input"
 />
 </label>

 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 Role
 <select
 className={INPUT}
 value={role}
 onChange={(e) => setRole(e.currentTarget.value as ScopeRole)}
 disabled={createMut.isPending}
 data-testid="conversion-crud-view-edit-reject-permission-scope-role-select"
 >
 {ROLES.map((r) => (
 <option key={r} value={r}>
 {r}
 </option>
 ))}
 </select>
 </label>

 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 Action
 <select
 className={INPUT}
 value={action}
 onChange={(e) => setAction(e.currentTarget.value as ScopeAction)}
 disabled={createMut.isPending}
 data-testid="conversion-crud-view-edit-reject-permission-scope-action-select"
 >
 {ACTIONS.map((a) => (
 <option key={a} value={a}>
 {a}
 </option>
 ))}
 </select>
 </label>

 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 Allowed
 <select
 className={INPUT}
 value={allowed ? "1" : "0"}
 onChange={(e) => setAllowed(e.currentTarget.value === "1")}
 disabled={createMut.isPending}
 data-testid="conversion-crud-view-edit-reject-permission-scope-allowed-select"
 >
 <option value="1">allow</option>
 <option value="0">deny</option>
 </select>
 </label>

 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700 sm:col-span-2">
 Reason (optional)
 <input
 type="text"
 className={INPUT}
 placeholder="e.g. out-of-scope region"
 value={reason}
 onChange={(e) => setReason(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="conversion-crud-view-edit-reject-permission-scope-reason-input"
 />
 </label>

 <div className="sm:col-span-2">
 <button
 type="submit"
 className={BTN}
 disabled={createMut.isPending || !conversionId.trim()}
 data-testid="conversion-crud-view-edit-reject-permission-scope-submit"
 >
 {createMut.isPending ? "Saving." : "Create rule"}
 </button>
 </div>
 </form>
 {createMut.error ? (
 <p className="mt-2 text-xs text-red-600" role="alert">
 {createMut.error.message}
 </p>
 ) : null}
 </section>

 <section
 className={CARD}
 aria-label="Conversion permission scope list"
 >
 <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Rules</h2>
 <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
 <input
 type="text"
 className={INPUT}
 placeholder="filter by conversion_id"
 value={filterConversionId}
 onChange={(e) => setFilterConversionId(e.currentTarget.value)}
 data-testid="conversion-crud-view-edit-reject-permission-scope-filter-conversion-id"
 />
 <select
 className={INPUT}
 value={filterRole}
 onChange={(e) =>
 setFilterRole(e.currentTarget.value as "" | ScopeRole)
 }
 data-testid="conversion-crud-view-edit-reject-permission-scope-filter-role"
 >
 <option value="">any role</option>
 {ROLES.map((r) => (
 <option key={r} value={r}>
 {r}
 </option>
 ))}
 </select>
 <select
 className={INPUT}
 value={filterAction}
 onChange={(e) =>
 setFilterAction(e.currentTarget.value as "" | ScopeAction)
 }
 data-testid="conversion-crud-view-edit-reject-permission-scope-filter-action"
 >
 <option value="">any action</option>
 {ACTIONS.map((a) => (
 <option key={a} value={a}>
 {a}
 </option>
 ))}
 </select>
 </div>
 </div>

 <div className="mt-4">
 {listQuery.isLoading ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="conversion-crud-view-edit-reject-permission-scope-loading"
 >
 Loading.
 </p>
 ) : listQuery.error ? (
 <p className="text-sm text-red-600" role="alert">
 {listQuery.error.message}
 </p>
 ) : items.length === 0 ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="conversion-crud-view-edit-reject-permission-scope-empty"
 >
 No rules yet.
 </p>
 ) : (
 <div
 className="overflow-x-auto"
 data-testid="conversion-crud-view-edit-reject-permission-scope-list"
 >
 <table className="w-full text-left text-sm">
 <thead className="border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
 <tr>
 <th className="py-2 pr-3">Conversion</th>
 <th className="py-2 pr-3">Role</th>
 <th className="py-2 pr-3">Action</th>
 <th className="py-2 pr-3">Allowed</th>
 <th className="py-2 pr-3">Reason</th>
 <th className="py-2 pr-3">Created</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-zinc-100">
 {items.map((row) => (
 <tr
 key={row.id}
 data-testid={`conversion-crud-view-edit-reject-permission-scope-row-${row.id}`}
 >
 <td className="py-2 pr-3 font-mono text-xs text-zinc-700">
 {row.conversion_id}
 </td>
 <td className="py-2 pr-3 text-zinc-700">{row.role}</td>
 <td className="py-2 pr-3 text-zinc-700">{row.action}</td>
 <td className="py-2 pr-3">
 <span
 className={
 row.allowed === 1 ? PILL_ALLOW : PILL_DENY
 }
 >
 {row.allowed === 1 ? "allow" : "deny"}
 </span>
 </td>
 <td className="py-2 pr-3 text-zinc-600">
 {row.reason ?? "—"}
 </td>
 <td className="py-2 pr-3 font-mono text-xs text-zinc-500">
 {fmtDate(row.created_at)}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 </section>
 </main>
	);
}

export default ConversionCrudViewEditRejectPermissionScopePage;
